import { BehaviorSubject, Subject, Subscription, from } from 'rxjs';
import { first } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { ToastService, ErrorSinkService } from 'ngx-zcs-components';
import {
  AppsyncModbusResponse,
  CallAPISolarmanDeviceInfoResponse,
  CommunicationChannel,
  TypeInternetDevice,
  SolarmanDeviceStatus,
  encodeMultiQuery,
  decodeMultiQuery,
} from 'ngx-modbus';
import { GetCommunicationChannelForStickQuery, Operation, Channel, OnDevicesAPISubscription } from 'ngx-core-services';
import { ModbusLink, INTERNET_RETRY_POLICY } from 'ngx-modbus';
import { queryCommunicationChannelForStick } from '../lib/upgrade-inverter/upgrade-inverter.query';
import { SolarmanService } from './solarman.service';
import { blockServiceLabels } from '../block-translations.service';
import { solarmanModbusResponse } from './graphql-fragments';
import { resolveInverterVersion } from './utils';

const BASE_ZCS_DEBUG = { ENABLED: true };
const baseLog = (msg: string, data?: unknown) => BASE_ZCS_DEBUG.ENABLED && console.log(`[Modbus:BaseZCS] ${msg}`, data ?? '');

export type ChannelStatusData = {
  CommunicationChannel: Channel;
  LoggerStatus: string;
};

export abstract class BaseZCSChannelService implements CommunicationChannel {
  readonly connectionStatus$ = new BehaviorSubject<boolean>(false);
  readonly response$ = new Subject<Uint8Array>();
  readonly responseByAppsync$ = new Subject<AppsyncModbusResponse>();

  protected responseSubscription$?: Subscription;
  protected deviceParams?: CallAPISolarmanDeviceInfoResponse;
  command?: ModbusLink | undefined;
  protected internetDevice?: TypeInternetDevice;

  abstract readonly refreshByDefault: number;
  protected abstract readonly maxTimeoutInMs: number;
  protected abstract readonly modbusAddress: number;
  protected abstract readonly delayBetweenCommandsInMs: number;

  abstract interaction(cmd: Uint8Array): Promise<Uint8Array>;

  protected abstract validateChannel(data: ChannelStatusData): void;

  concurrentQuery = {
    count: 256,
    encodeMultiQuery,
    decodeMultiQuery,
  };

  constructor(
    protected readonly getCommunicationChannelForStickQuery: GetCommunicationChannelForStickQuery,
    protected readonly onDevicesAPISubscription: OnDevicesAPISubscription,
    protected readonly router: Router,
    protected readonly solarmanService: SolarmanService,
    protected readonly toastService: ToastService,
    protected readonly viewportScroller: ViewportScroller,
    protected readonly errorSink: ErrorSinkService,
  ) {
    this.interaction = this.interaction.bind(this);
    this.close = this.close.bind(this);
  }

  get isConnected(): boolean {
    return true;
  }

  get deviceId() {
    if (this.internetDevice?.serial) {
      return this.internetDevice.serial;
    }
    return (this.deviceParams?.communication.childList?.length || 0) > 1
      ? this.deviceParams?.communication.childList.filter((x) => x.deviceSn.length === 20)[0].deviceSn
      : this.deviceParams?.communication.childList?.[0].deviceSn;
  }

  get loggerId(): number | undefined {
    return this.deviceParams?.communication.childList[0].deviceId || this.internetDevice?.loggerId;
  }

  get loggerSn(): string | undefined {
    return this.deviceParams?.communication?.deviceSn;
  }

  get inverterId() {
    if (this.internetDevice?.serial) {
      return this.internetDevice.serial;
    }
    const deviceIds = this.deviceParams?.communication.childList.filter((x) => x.deviceType === 'INVERTER');
    return (deviceIds?.length || 0) > 1
      ? deviceIds?.filter((x) => x.deviceSn.length === 20)[0].deviceSn
      : deviceIds?.[0].deviceSn;
  }

  readonly scan = async () => { throw new Error('Method not implemented.'); };

  async close(): Promise<void> {
    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
    }
    this.command?.destroy();

    this.deviceParams = undefined;
    this.command = undefined;
  }


  protected async openByInverterSerial(serial: string): Promise<void> {
    try {
      this.deviceParams = await this.solarmanService.deviceInfoRequest(serial);
      if (this.deviceParams?.communication.childList === null) {
        throw new Error('Logger not found');
      }
    } catch (error) {
      baseLog('openByInverterSerial error:', error);
      try {
        await this.solarmanService.addLoggerToSolarmanByInverterSerial(serial);
        this.deviceParams = await this.solarmanService.deviceInfoRequest(serial);
      } catch {
        await this.toastService.persistence(blockServiceLabels.loggerNotFoundError , 'Error');
        throw new Error('Logger not found');
      }
    }

    const onlyNetwork = true;
    const channels = await this.getCommunicationChannelForStickQuery.execute(
      queryCommunicationChannelForStick,
      {
        loggerId: this.loggerSn!,
        operation: Operation.MODBUS,
      },
      onlyNetwork,
    );

    const data = channels.data.getCommunicationChannelForStick as ChannelStatusData;

    this.validateChannel(data);

    const deviceIds = this.deviceParams?.communication.childList
      .filter((x) => x.deviceType === 'INVERTER')
      .filter((x) => serial === x.deviceSn)
      .filter((x) => [
        SolarmanDeviceStatus.Online,
        SolarmanDeviceStatus.Alarm,
      ].includes(x.deviceState));

    const selectedDevice = deviceIds?.length > 1
      ? deviceIds.filter((x) => x.deviceSn.length === 20)?.[0]
      : deviceIds?.[0];

    const deviceId = selectedDevice?.deviceId;

    if (deviceId && this.loggerSn) {
      this.responseSubscription$ = from(
        await this.onDevicesAPISubscription.execute(
          solarmanModbusResponse,
          { deviceId: Number(this.loggerSn) },
        ).pipe(first()).toPromise() as any,
      ).subscribe((result: any) => {
        const res = (result).data.onDevicesAPI as AppsyncModbusResponse;
        this.responseByAppsync$.next(res);
      });

      const handleFatalError = async () => {
        this.errorSink.reportTransport(
          blockServiceLabels.genericErrorMessage || 'Sorry, something went wrong',
          `Fatal error on channel for device ${serial}`,
        );
        await this.close();
        if (this.internetDevice?.onFatalError) {
          this.internetDevice.onFatalError();
          return;
        }
        await this.router.navigate(['/']).then(() => {
          this.viewportScroller.scrollToPosition([0, 0]);
        });
      };

      const inverterVersion = resolveInverterVersion(serial);
      baseLog(` ModbusLink: serial="${serial}", inverterVersion=${inverterVersion ?? 'legacy'}`);

      this.command = new ModbusLink({
        deviceId: serial,
        inverterVersion,
        concurrentQuery: this.concurrentQuery,
        executeCmd: this.interaction,
        maxTimeoutInMs: this.maxTimeoutInMs,
        modbusAddress: this.modbusAddress,
        handleFatalError,
        delayBetweenCommandsInMs: this.delayBetweenCommandsInMs,
        retryPolicy: INTERNET_RETRY_POLICY,
      });
    }
  }

  async open(internetDevice: TypeInternetDevice): Promise<void> {
    this.internetDevice = internetDevice;
    await this.solarmanService.authenticate();

    if (this.responseSubscription$) {
      this.responseSubscription$.unsubscribe();
    }

    this.command?.destroy();

    if (internetDevice.serial) {
      await this.openByInverterSerial(internetDevice.serial);
    } else if (internetDevice.loggerId) {
      try {
        const inverterSerial = await this.solarmanService.getInverterSerialFromLogger(`${internetDevice.loggerId}`);
        if (inverterSerial) {
          await this.openByInverterSerial(inverterSerial);
        }
      } catch (error: any) {
        this.toastService.persistence(error?.message, 'Error');
      }
    }
  }
}
